import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Grid,
  Divider,
  FormHelperText,
  InputAdornment,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Checkbox,
  CardHeader,
  useTheme,
  useMediaQuery,
  ListItemButton,
  Card,
  CardContent,
  DialogActions,
  DialogContent,
  DialogTitle,
  Dialog,
} from "@mui/material";
import {
  Send as SendIcon,
  Notifications as NotificationsIcon,
  CorporateFare as CorporateIcon,
  Person as PersonIcon,
  DriveEta as DriverIcon,
  TwoWheeler as RiderIcon,
  Title as TitleIcon,
  Message as MessageIcon,
} from "@mui/icons-material";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import useHttp from "../../Hooks/use-http";
import { littleGreen, littleRed } from "../CommonComponent/DefineColors";

const SendNotification = () => {
  const history = useHistory();
  const location = useLocation();
  const { sendRequest } = useHttp();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [formData, setFormData] = useState({
    title: "",
    data: "",
    message: "",
    appName: "all",
    corporateID: "",
    driverEmailIDs: [],
    riderMobileNumbers: [],
  });

  const [corporates, setCorporates] = useState([]);
  const [filteredCorporates, setFilteredCorporates] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showCustomSelection, setShowCustomSelection] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [recipientCount, setRecipientCount] = useState(0);

  // Fetch corporates on component mount
  useEffect(() => {
    fetchCorporates();
  }, []);

  // Use effect to check for passed notification data
  useEffect(() => {
    if (location.state && location.state.notificationData) {
      const { notificationData } = location.state;
      const dataString = JSON.stringify(notificationData?.data);
      setFormData((prev) => ({
        ...prev,
        title: notificationData.title || "",
        data: dataString || "",
        message: notificationData.message || "",
        appName: notificationData.appName || "all",
        corporateID: notificationData.corporateID || "",
        driverEmailIDs: notificationData.driverEmailIDs || [],
        riderMobileNumbers: notificationData.riderMobileNumbers || [],
      }));

      // Clear the state to avoid re-pre-filling on refresh
      history.replace({ ...location, state: undefined });
    }
  }, [location, history]);

  // Filter corporates when they are loaded
  useEffect(() => {
    if (corporates.length > 0) {
      const filtered = corporates.filter(
        (corporate) => !corporate.parentID || corporate.parentID === "",
      );
      setFilteredCorporates(filtered);
    }
  }, [corporates]);

  // Show/hide custom selection based on appName
  useEffect(() => {
    setShowCustomSelection(formData.appName === "custom");

    // Reset custom selections when not in custom mode
    if (formData.appName !== "custom") {
      setFormData((prev) => ({
        ...prev,
        corporateID: "",
        driverEmailIDs: [],
        riderMobileNumbers: [],
      }));
    }
  }, [formData.appName]);

  // Fetch drivers and riders when corporate is selected
  useEffect(() => {
    if (formData.corporateID && formData.appName === "custom") {
      fetchDrivers();
      fetchRiders();
    } else {
      setDrivers([]);
      setRiders([]);
      setFormData((prev) => ({
        ...prev,
        driverEmailIDs: [],
        riderMobileNumbers: [],
      }));
    }
  }, [formData.corporateID, formData.appName]);

  const fetchCorporates = () => {
    sendRequest(
      {
        url: "/corporates",
        method: "POST",
        body: {
          emailID: localStorage.getItem("user"),
        },
      },
      (data) => {
        setCorporates(data.corporates || []);
      },
      (error) => {
        toast.error("Failed to load corporates");
        console.error("Error fetching corporates:", error);
      },
    );
  };

  const fetchDrivers = () => {
    sendRequest(
      {
        url: "/corporatedrivers",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: {
          emailID: localStorage.getItem("user"),
          data: {
            filter: { corporateID: formData.corporateID },
          },
        },
      },
      (data) => {
        setDrivers(data.DriverList || []);
      },
      (error) => {
        toast.error("Failed to load drivers");
        console.error("Error fetching drivers:", error);
      },
    );
  };

  const fetchRiders = () => {
    sendRequest(
      {
        url: "/corporateriders",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: {
          emailID: localStorage.getItem("user"),
          data: {
            filter: { corporateID: formData.corporateID },
          },
        },
      },
      (data) => {
        setRiders(data?.corporateRiders || []);
      },
      (error) => {
        toast.error("Failed to load riders");
        console.error("Error fetching riders:", error);
      },
    );
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear errors when field changes
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    }

    if (formData.appName === "custom" && !formData.corporateID) {
      newErrors.corporateID =
        "Corporate selection is required for custom notifications";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const calculateRecipientCount = () => {
    switch (formData.appName) {
      case "all":
        return "All users";
      case "driver":
        return "All drivers";
      case "rider":
        return "All riders";
      case "custom":
        const driverCount = formData.driverEmailIDs.length;
        const riderCount = formData.riderMobileNumbers.length;
        const total = driverCount + riderCount;
        return `${total} recipients (${driverCount} drivers, ${riderCount} riders)`;
      default:
        return "0 recipients";
    }
  };

  const handleSubmitConfirmation = () => {
    if (!validateForm()) {
      return;
    }

    // Calculate recipient count for confirmation
    const countText = calculateRecipientCount();
    setRecipientCount(countText);
    setConfirmationOpen(true);
  };

  const handleConfirmSend = () => {
    setConfirmationOpen(false);
    sendNotification();
  };

  const sendNotification = () => {
    setLoading(true);

    // Parse the data string back to an object if it exists
    let dataObj = null;
    try {
      dataObj = formData.data ? JSON.parse(formData.data) : null;
    } catch (error) {
      toast.error("Invalid JSON format in Data field");
      setLoading(false);
      return;
    }

    // Prepare the data to send based on backend API requirements
    let notificationData = {
      title: formData.title,
      message: formData.message,
      appName: formData.appName,
      data: formData?.data,
      createdBy: localStorage.getItem("user"),
    };

    // For custom selection, add the specific email IDs and mobile numbers
    if (formData.appName === "custom") {
      notificationData = {
        ...notificationData,
        driverEmailIDs: formData.driverEmailIDs,
        riderMobileNumbers: formData.riderMobileNumbers,
      };
    }

    // Send the notification
    sendRequest(
      {
        url: "/multipleNotificationForWeb",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: notificationData,
      },
      (response) => {
        setLoading(false);
        if (response.status == "200" || response.status == "201") {
          toast.success("Notification sent successfully!");
          history.push("/notifications");
        } else {
          toast.error("Failed to send notification");
        }
      },
      (error) => {
        setLoading(false);
        toast.error("Failed to send notification");
        console.error("Error sending notification:", error);
      },
    );
  };
  //   const handleSubmit = () => {
  //     if (!validateForm()) {
  //       return;
  //     }

  //     setLoading(true);

  //     // Parse the data string back to an object if it exists
  //     let dataObj = null;
  //     try {
  //       dataObj = formData.data ? JSON.parse(formData.data) : null;
  //     } catch (error) {
  //       toast.error("Invalid JSON format in Data field");
  //       setLoading(false);
  //       return;
  //     }

  //     // Prepare the data to send based on backend API requirements
  //     let notificationData = {
  //       title: formData.title,
  //       message: formData.message,
  //       appName: formData.appName,
  //       data: formData?.data,
  //       //   data: dataObj,
  //     };

  //     // For custom selection, add the specific email IDs and mobile numbers
  //     if (formData.appName === "custom") {
  //       notificationData = {
  //         ...notificationData,
  //         driverEmailIDs: formData.driverEmailIDs,
  //         riderMobileNumbers: formData.riderMobileNumbers,
  //       };
  //     }

  //     // Send the notification
  //     sendRequest(
  //       {
  //         url: "/multipleNotificationForWeb",
  //         method: "POST",
  //         headers: {
  //           "Content-Type": "application/json",
  //         },
  //         body: notificationData,
  //       },
  //       (response) => {
  //         setLoading(false);
  //         if (response.status == "200" || response.status == "201") {
  //           toast.success("Notification sent successfully!");
  //           history.push("/notifications");
  //         } else {
  //           toast.error("Failed to send notification");
  //         }
  //       },
  //       (error) => {
  //         setLoading(false);
  //         toast.error("Failed to send notification");
  //         console.error("Error sending notification:", error);
  //       }
  //     );
  //   };

  // Select all drivers
  const handleSelectAllDrivers = () => {
    if (formData.driverEmailIDs.length === drivers.length) {
      // If all are selected, deselect all
      handleChange("driverEmailIDs", []);
    } else {
      // Select all drivers
      const allDriverEmails = drivers.map((driver) => driver.emailID);
      handleChange("driverEmailIDs", allDriverEmails);
    }
  };

  // Select all riders
  const handleSelectAllRiders = () => {
    if (formData.riderMobileNumbers.length === riders.length) {
      // If all are selected, deselect all
      handleChange("riderMobileNumbers", []);
    } else {
      // Select all riders
      const allRiderNumbers = riders.map((rider) => rider.mobileNumber);
      handleChange("riderMobileNumbers", allRiderNumbers);
    }
  };

  return (
    <Box sx={{ maxWidth: 1300, margin: "auto", p: isMobile ? 2 : 3 }}>
      <Typography
        variant="h6"
        component="h6"
        gutterBottom
        align="left"
        sx={{
          mb: 1,
          fontWeight: 600,
          background:
            "linear-gradient(45deg,rgb(12, 55, 90) 30%,rgb(33, 77, 87) 90%)",
          backgroundClip: "text",
          textFillColor: "transparent",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        SEND NOTIFICATION
      </Typography>

      <Paper
        elevation={2}
        sx={{
          p: isMobile ? 2 : 3,
          borderRadius: 2,
          background: "linear-gradient(to bottom right, #f9f9f9, #ffffff)",
        }}
      >
        <Grid container spacing={2}>
          {/* Left side - Title and Audience Type */}
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Notification Title"
                  value={formData.title}
                  onChange={(e) => handleChange("title", e.target.value)}
                  error={!!errors.title}
                  helperText={errors.title}
                  required
                  variant="standard"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <TitleIcon color="action" sx={{ fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {localStorage.getItem("user") ===
                "upendra.shah@little.global" && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Data"
                    value={formData.data}
                    onChange={(e) => handleChange("data", e.target.value)}
                    error={!!errors.title}
                    helperText={errors.title}
                    variant="standard"
                    //   InputProps={{
                    //     startAdornment: (
                    //       <InputAdornment position="start">
                    //         <TitleIcon color="action" sx={{ fontSize: 20 }} />
                    //       </InputAdornment>
                    //     ),
                    //   }}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <FormControl fullWidth variant="standard">
                  <InputLabel>Audience Type</InputLabel>
                  <Select
                    value={formData.appName}
                    label="Audience Type"
                    onChange={(e) => handleChange("appName", e.target.value)}
                    variant="standard"
                  >
                    <MenuItem value="all">
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <PersonIcon sx={{ mr: 1, fontSize: 20 }} />
                        All Users
                      </Box>
                    </MenuItem>
                    <MenuItem value="driver">
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <DriverIcon sx={{ mr: 1, fontSize: 20 }} />
                        All Drivers
                      </Box>
                    </MenuItem>
                    <MenuItem value="rider">
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <RiderIcon sx={{ mr: 1, fontSize: 20 }} />
                        All Riders
                      </Box>
                    </MenuItem>
                    <MenuItem value="custom">
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <CorporateIcon sx={{ mr: 1, fontSize: 20 }} />
                        Custom Selection
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {showCustomSelection && (
                <Grid item xs={12}>
                  <FormControl
                    fullWidth
                    error={!!errors.corporateID}
                    variant="standard"
                  >
                    <InputLabel>Select Corporate</InputLabel>
                    <Select
                      value={formData.corporateID}
                      label="Select Corporate"
                      variant="standard"
                      onChange={(e) =>
                        handleChange("corporateID", e.target.value)
                      }
                    >
                      {filteredCorporates.map((corporate) => (
                        <MenuItem
                          key={corporate.corporateID}
                          value={corporate.corporateID}
                        >
                          {corporate.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.corporateID && (
                      <FormHelperText>{errors.corporateID}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </Grid>

          {/* Right side - Message Content */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Message Content"
              value={formData.message}
              onChange={(e) => handleChange("message", e.target.value)}
              error={!!errors.message}
              helperText={errors.message}
              multiline
              rows={6}
              required
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MessageIcon color="action" sx={{ fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{ height: "100%" }}
            />
          </Grid>

          {showCustomSelection && formData.corporateID && (
            <>
              {/* <Grid item xs={12}>
                <Typography
                  variant="subtitle1"
                  component="h3"
                  sx={{ mb: 1, mt: 2 }}
                >
                  Select Recipients
                </Typography>
              </Grid> */}

              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: "100%" }}>
                  <CardContent sx={{ p: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 1.5,
                        borderBottom: 1,
                        borderColor: "divider",
                      }}
                    >
                      <Typography
                        variant="body2"
                        component="h4"
                        fontWeight="medium"
                      >
                        Drivers
                      </Typography>
                      <Button
                        size="small"
                        onClick={handleSelectAllDrivers}
                        sx={{ minWidth: "auto", px: 1 }}
                      >
                        {formData.driverEmailIDs.length === drivers.length
                          ? "Deselect All"
                          : "Select All"}
                      </Button>
                    </Box>
                    <Box sx={{ maxHeight: 200, overflow: "auto" }}>
                      <List dense sx={{ py: 0 }}>
                        {drivers.map((driver) => (
                          <ListItem
                            key={driver.emailID}
                            disablePadding
                            sx={{
                              borderBottom: "1px solid",
                              borderColor: "divider",
                            }}
                          >
                            <ListItemButton
                              sx={{ py: 0.5 }}
                              onClick={() => {
                                const newDriverEmailIDs =
                                  formData.driverEmailIDs.includes(
                                    driver.emailID,
                                  )
                                    ? formData.driverEmailIDs.filter(
                                        (email) => email !== driver.emailID,
                                      )
                                    : [
                                        ...formData.driverEmailIDs,
                                        driver.emailID,
                                      ];
                                handleChange(
                                  "driverEmailIDs",
                                  newDriverEmailIDs,
                                );
                              }}
                            >
                              <Checkbox
                                size="small"
                                checked={formData.driverEmailIDs.includes(
                                  driver.emailID,
                                )}
                                tabIndex={-1}
                                disableRipple
                              />
                              <ListItemAvatar sx={{ minWidth: 40 }}>
                                <Avatar sx={{ width: 28, height: 28 }}>
                                  <DriverIcon sx={{ fontSize: 16 }} />
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={driver.fName + " " + driver.lName}
                                secondary={driver.emailID}
                                primaryTypographyProps={{
                                  variant: "body2",
                                }}
                                secondaryTypographyProps={{
                                  variant: "caption",
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: "100%" }}>
                  <CardContent sx={{ p: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 1.5,
                        borderBottom: 1,
                        borderColor: "divider",
                      }}
                    >
                      <Typography
                        variant="body2"
                        component="h4"
                        fontWeight="medium"
                      >
                        Riders
                      </Typography>
                      <Button
                        size="small"
                        onClick={handleSelectAllRiders}
                        sx={{ minWidth: "auto", px: 1 }}
                      >
                        {formData.riderMobileNumbers.length === riders.length
                          ? "Deselect All"
                          : "Select All"}
                      </Button>
                    </Box>
                    <Box sx={{ maxHeight: 200, overflow: "auto" }}>
                      <List dense sx={{ py: 0 }}>
                        {riders.map((rider) => (
                          <ListItem
                            key={rider.mobileNumber}
                            disablePadding
                            sx={{
                              borderBottom: "1px solid",
                              borderColor: "divider",
                            }}
                          >
                            <ListItemButton
                              sx={{ py: 0.5 }}
                              onClick={() => {
                                const newRiderMobileNumbers =
                                  formData.riderMobileNumbers.includes(
                                    rider.mobileNumber,
                                  )
                                    ? formData.riderMobileNumbers.filter(
                                        (mobile) =>
                                          mobile !== rider.mobileNumber,
                                      )
                                    : [
                                        ...formData.riderMobileNumbers,
                                        rider.mobileNumber,
                                      ];
                                handleChange(
                                  "riderMobileNumbers",
                                  newRiderMobileNumbers,
                                );
                              }}
                            >
                              <Checkbox
                                size="small"
                                checked={formData.riderMobileNumbers.includes(
                                  rider.mobileNumber,
                                )}
                                tabIndex={-1}
                                disableRipple
                              />
                              <ListItemAvatar sx={{ minWidth: 40 }}>
                                <Avatar sx={{ width: 28, height: 28 }}>
                                  <RiderIcon sx={{ fontSize: 16 }} />
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={rider.riderName}
                                secondary={rider.mobileNumber}
                                primaryTypographyProps={{
                                  variant: "body2",
                                }}
                                secondaryTypographyProps={{
                                  variant: "caption",
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                size="medium"
                // onClick={handleSubmit}
                onClick={handleSubmitConfirmation}
                disabled={loading}
                sx={{
                  py: 1,
                  px: 3,
                  background: littleGreen,
                  boxShadow: "0 2px 4px rgba(33, 150, 243, 0.4)",
                  borderRadius: 1,
                  fontWeight: 500,
                  fontSize: "0.9rem",
                  "&:hover": {
                    boxShadow: "0 4px 8px rgba(33, 150, 243, 0.5)",
                    transform: "translateY(-1px)",
                  },
                  transition: "all 0.2s ease-in-out",
                }}
              >
                {loading ? "Sending..." : "Send Notification"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Dialog
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <NotificationsIcon sx={{ mr: 1, color: "primary.main" }} />
            Confirm Notification
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            This notification will be sent to: <strong>{recipientCount}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to send this notification?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setConfirmationOpen(false)}
            color="inherit"
            sx={{
              background: littleRed,
              color: "#fff",
              padding: "6px 16px",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                background: "black", // lighter red for hover
                // padding: "8px 20px", // slightly increased padding on hover
                boxShadow: "0 2px 4px rgba(211, 47, 47, 0.4)",
                transform: "translateY(-1px)",
                color: "#fff",
              },
              "&:active": {
                transform: "translateY(0)",
              },
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSend}
            variant="contained"
            disabled={loading}
            sx={{
              background: littleGreen,
              transition: "all 0.2s ease-in-out",
              "&:hover": { transform: "translateY(-1px)" },
            }}
          >
            {loading ? "Sending..." : "Send"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SendNotification;
